import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import * as S from './Contact.styles';
import { CDN_BASE_URL } from '../../config';

const Contact = () => {
  const [isFormLoading, setIsFormLoading] = useState(true);
  const [selectedMapCampus, setSelectedMapCampus] = useState(0);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [imageLoaded, setImageLoaded] = useState({});
  const [imageError, setImageError] = useState({});
  const [isCampusExpanded, setIsCampusExpanded] = useState(false); // 新增状态
  const [qrModalImage, setQrModalImage] = useState(null); // QR码放大弹窗
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);

  const campusData = useMemo(() => [
    {
      name: "Victoria Park Campus",
      address: "Unit 4/786 Albany Hwy, East Victoria Park WA 6101",
      coordinates: { lat: -31.983347399840746, lng: 115.90239997752798 },
      googleMapsUrl: "https://www.google.com/maps/search/?api=1&query=Unit+4%2F786+Albany+Hwy%2C+East+Victoria+Park+WA+6101",
      appleMapsUrl: "https://maps.apple.com/?q=Unit+4/786+Albany+Hwy,+East+Victoria+Park+WA+6101",
      image: `${CDN_BASE_URL}/static/campus_vicpark.jpg`,
      phone: '0432528596',
      qrcodeUrl: `${CDN_BASE_URL}/static/qrcode/vicpark.jpg`
    },
    {
      name: "Claremont Campus", 
      address: "Level 1/229 Stirling Hwy, Claremont WA 6010",
      coordinates: { lat: -31.981604430133256, lng: 115.78825899716978 },
      googleMapsUrl: "https://www.google.com/maps/search/?api=1&query=Level+1%2F229+Stirling+Hwy%2C+Claremont+WA+6010",
      appleMapsUrl: "https://maps.apple.com/?q=Level+1/229+Stirling+Hwy,+Claremont+WA+6010",
      image: `${CDN_BASE_URL}/static/campus_claremont.jpg`,
      phone: '0452012359',
      qrcodeUrl: `${CDN_BASE_URL}/static/qrcode/claremont.jpg`
    },
    {
      name: "Osborne Park Campus",
      address: "342 Scarborough Beach Road, Osborne Park WA 6017",
      coordinates: { lat: -31.913966827511008, lng: 115.82148713079465 },
      googleMapsUrl: "https://www.google.com/maps/search/?api=1&query=342+Scarborough+Beach+Road%2C+Osborne+Park+WA+6017",
      appleMapsUrl: "https://maps.apple.com/?q=342+Scarborough+Beach+Road,+Osborne+Park+WA+6017",
      image: null, // 暂时没有图片
      phone: '0450098630',
      qrcodeUrl: `${CDN_BASE_URL}/static/qrcode/osbornpark.jpg`
    }
  ], []);

  const handleImageLoad = (campusIndex) => {
    setImageLoaded(prev => ({ ...prev, [campusIndex]: true }));
  };

  const handleImageError = (campusIndex) => {
    setImageError(prev => ({ ...prev, [campusIndex]: true }));
  };

  const updateMapMarker = useCallback(async (campusIndex) => {
    if (!mapInstanceRef.current || !window.google) return;

    try {
      const { AdvancedMarkerElement } = await window.google.maps.importLibrary("marker");

      // Clear existing markers
      markersRef.current.forEach(marker => marker.map = null);
      markersRef.current = [];

      const campus = campusData[campusIndex];
      
      // Create new marker
      const marker = new AdvancedMarkerElement({
        map: mapInstanceRef.current,
        position: campus.coordinates,
        title: campus.name,
      });

      markersRef.current.push(marker);

      // Center map on the new campus
      mapInstanceRef.current.setCenter(campus.coordinates);
      mapInstanceRef.current.setZoom(15);

      // Create info window
      const infoWindow = new window.google.maps.InfoWindow({
        content: `
          <div style="padding: 10px; max-width: 200px;">
            <h3 style="margin: 0 0 8px 0; font-size: 14px; font-weight: 600;">${campus.name}</h3>
            <p style="margin: 0; font-size: 12px; color: #666; line-height: 1.4;">${campus.address}</p>
          </div>
        `
      });

      marker.addListener("click", () => {
        infoWindow.open(mapInstanceRef.current, marker);
      });
    } catch (error) {
      console.error("Error updating marker:", error);
    }
  }, [campusData]);

  const initializeMap = useCallback(async () => {
    if (!mapRef.current || !window.google) return;

    try {
      const { Map } = await window.google.maps.importLibrary("maps");

      const map = new Map(mapRef.current, {
        zoom: 15,
        center: campusData[selectedMapCampus].coordinates,
        mapId: "DEMO_MAP_ID",
        disableDefaultUI: false,
        zoomControl: true,
        streetViewControl: false,
        fullscreenControl: false,
      });

      mapInstanceRef.current = map;
      setIsMapLoaded(true);
      updateMapMarker(selectedMapCampus);
    } catch (error) {
      console.error("Error initializing map:", error);
    }
  }, [campusData, selectedMapCampus, updateMapMarker]);

  // Load Google Maps
  useEffect(() => {
    const loadGoogleMaps = () => {
      if (window.google && window.google.maps) {
        initializeMap();
        return;
      }

      // Check if script is already loading
      if (document.querySelector('script[src*="maps.googleapis.com"]')) {
        const checkGoogle = setInterval(() => {
          if (window.google && window.google.maps) {
            clearInterval(checkGoogle);
            initializeMap();
          }
        }, 100);
        return;
      }

      // Load the Google Maps script
      const script = document.createElement('script');
      script.innerHTML = `
        (g=>{var h,a,k,p="The Google Maps JavaScript API",c="google",l="importLibrary",q="__ib__",m=document,b=window;b=b[c]||(b[c]={});var d=b.maps||(b.maps={}),r=new Set,e=new URLSearchParams,u=()=>h||(h=new Promise(async(f,n)=>{await (a=m.createElement("script"));e.set("libraries",[...r]+"");for(k in g)e.set(k.replace(/[A-Z]/g,t=>"_"+t[0].toLowerCase()),g[k]);e.set("callback",c+".maps."+q);a.src=\`https://maps.\${c}apis.com/maps/api/js?\`+e;d[q]=f;a.onerror=()=>h=n(Error(p+" could not load."));a.nonce=m.querySelector("script[nonce]")?.nonce||"";m.head.append(a)}));d[l]?console.warn(p+" only loads once. Ignoring:",g):d[l]=(f,...n)=>r.add(f)&&u().then(()=>d[l](f,...n))})({
          key: "AIzaSyCSY884TuoGjkgrv-a6X9idO-d53rAa3Z8",
          v: "weekly",
        });
      `;
      document.head.appendChild(script);

      // Wait for Google Maps to load
      const checkGoogle = setInterval(() => {
        if (window.google && window.google.maps) {
          clearInterval(checkGoogle);
          initializeMap();
        }
      }, 100);
    };

    loadGoogleMaps();
  }, [initializeMap]);

  // Update map when campus selection changes
  useEffect(() => {
    if (isMapLoaded) {
      updateMapMarker(selectedMapCampus);
    }
  }, [selectedMapCampus, isMapLoaded, updateMapMarker]);

  const handleIframeLoad = () => {
    setIsFormLoading(false);
  };

  const handleAddressClick = (campus) => {
    const userAgent = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isAndroid = /Android/.test(userAgent);
    
    if (isIOS) {
      window.open(campus.appleMapsUrl, '_blank');
    } else if (isAndroid) {
      window.open(campus.googleMapsUrl, '_blank');
    } else {
      window.open(campus.googleMapsUrl, '_blank');
    }
  };

  return (
    <S.Container>
      <Helmet>
        <title>Uplus Education - Contact Us</title>
        <meta name="description" content="Get in touch with Uplus Education" />
      </Helmet>
      
      {/* Campus Information Section - Mobile */}
      <S.CampusInfoMobile>
        <S.CampusToggleHeader onClick={() => setIsCampusExpanded(!isCampusExpanded)}>
          <h2>Our Campus Locations</h2>
          <S.ToggleIcon isExpanded={isCampusExpanded}>
            {/* {isCampusExpanded ? '▲' : '▼'} */}
            ▲
          </S.ToggleIcon>
        </S.CampusToggleHeader>
        
        <S.CampusContent isExpanded={isCampusExpanded}>
          {campusData.map((campus, index) => (
            <S.CampusCard key={index}>
              {campus.image && (
                <S.CampusImageContainer>
                  {!imageLoaded[index] && !imageError[index] && (
                    <S.ImagePlaceholder>
                      <S.ImageLoadingText>Loading image...</S.ImageLoadingText>
                    </S.ImagePlaceholder>
                  )}
                  {!imageError[index] && (
                    <S.CampusImage
                      src={campus.image}
                      alt={`${campus.name} exterior`}
                      onLoad={() => handleImageLoad(index)}
                      onError={() => handleImageError(index)}
                      style={{ opacity: imageLoaded[index] ? 1 : 0 }}
                    />
                  )}
                  {imageError[index] && (
                    <S.ImagePlaceholder>
                      <S.ImageErrorText>📷 Image unavailable</S.ImageErrorText>
                    </S.ImagePlaceholder>
                  )}
                </S.CampusImageContainer>
              )}
              <S.CampusTextContent>                <S.CampusName>{campus.name}</S.CampusName>
                <S.CampusAddress 
                  onClick={() => handleAddressClick(campus)}
                  style={{ cursor: 'pointer', color: '#3949ab' }}
                >
                  {campus.address}
                  <S.MapIcon>📍</S.MapIcon>
                </S.CampusAddress>
                <S.InfoRow>
                  <S.InfoColumn>
                    {campus.phone && <S.CampusPhone>Contact: {campus.phone}</S.CampusPhone>}
                  </S.InfoColumn>
                  {campus.qrcodeUrl && (
                    <S.QRCodeContainer>
                      <div>
                        <S.QRCodeImage
                          src={campus.qrcodeUrl}
                          alt={`${campus.name} QR Code`}
                          loading="lazy"
                          onClick={() => setQrModalImage(campus.qrcodeUrl)}
                          style={{ cursor: 'pointer' }}
                        />
                      </div>
                    </S.QRCodeContainer>
                  )}
                </S.InfoRow>
              </S.CampusTextContent>
            </S.CampusCard>
          ))}
          <S.ContactEmail>
            <a href="mailto:info@uplusedu.com.au">
              info@uplusedu.com.au
            </a>
          </S.ContactEmail>
        </S.CampusContent>
      </S.CampusInfoMobile>

      <S.ContentWrapper>
        {/* Contact Form Section */}
        <S.FormSection>
          <div style={{ position: 'relative', width: '100%' }}>
            {isFormLoading && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '200px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f5f5f5',
                borderRadius: '8px',
                zIndex: 10,
                flexDirection: 'column',
                gap: '1rem'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  border: '4px solid #e0e0e0',
                  borderTop: '4px solid #3949ab',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                <p style={{ margin: 0, color: '#666', fontSize: '1.1rem' }}>Form Loading...</p>
              </div>
            )}
            
            <iframe
              src="https://docs.google.com/forms/d/e/1FAIpQLScw-WVqtQJL645MJR17kCTcVGEWR1IbtWCA7UO63rr4AvPA_Q/viewform?embedded=true"
              width="100%"
              height="1000"
              style={{ 
                border: 0, 
                display: 'block',
                opacity: isFormLoading ? 0 : 1,
                transition: 'opacity 0.3s ease-in-out'
              }}
              frameBorder="0"
              marginHeight="0"
              marginWidth="0"
              title="Contact Form"
              onLoad={handleIframeLoad}
            >
              Loading…
            </iframe>
          </div>
        </S.FormSection>

        {/* Campus Information Section - Desktop */}
        <S.CampusInfoDesktop>
          <h2>Our Campus Locations</h2>
          
          {/* Campus Selector */}
          <S.CampusSelector>
            {campusData.map((campus, index) => (
              <S.CampusButton
                key={index}
                isActive={selectedMapCampus === index}
                onClick={() => setSelectedMapCampus(index)}
              >
                {campus.name.replace(' Campus', '')}
              </S.CampusButton>
            ))}
          </S.CampusSelector>

          {/* Selected Campus Info */}
          <S.CampusCard>
            {campusData[selectedMapCampus].image && (
              <S.CampusImageContainer>
                {!imageLoaded[selectedMapCampus] && !imageError[selectedMapCampus] && (
                  <S.ImagePlaceholder>
                    <S.ImageLoadingText>Loading image...</S.ImageLoadingText>
                  </S.ImagePlaceholder>
                )}
                {!imageError[selectedMapCampus] && (
                  <S.CampusImage
                    src={campusData[selectedMapCampus].image}
                    alt={`${campusData[selectedMapCampus].name} exterior`}
                    onLoad={() => handleImageLoad(selectedMapCampus)}
                    onError={() => handleImageError(selectedMapCampus)}
                    style={{ opacity: imageLoaded[selectedMapCampus] ? 1 : 0 }}
                  />
                )}
                {imageError[selectedMapCampus] && (
                  <S.ImagePlaceholder>
                    <S.ImageErrorText>📷 Image unavailable</S.ImageErrorText>
                  </S.ImagePlaceholder>
                )}
              </S.CampusImageContainer>
            )}
            <S.CampusTextContent>              
              <S.InfoRow>
                <S.InfoColumn>
                  <S.CampusName>{campusData[selectedMapCampus].name}</S.CampusName>
                  <S.CampusAddress>{campusData[selectedMapCampus].address}</S.CampusAddress>
                  {campusData[selectedMapCampus].phone && <S.CampusPhone><b>Contact: {campusData[selectedMapCampus].phone}</b></S.CampusPhone>}
                </S.InfoColumn>
                {campusData[selectedMapCampus].qrcodeUrl && (
                  <S.QRCodeContainer>
                    <div>
                      <S.QRCodeImage
                        src={campusData[selectedMapCampus].qrcodeUrl}
                        alt={`${campusData[selectedMapCampus].name} QR Code`}
                        loading="lazy"
                        onClick={() => setQrModalImage(campusData[selectedMapCampus].qrcodeUrl)}
                        style={{ cursor: 'pointer' }}
                      />
                    </div>
                  </S.QRCodeContainer>
                )}
              </S.InfoRow>
            </S.CampusTextContent>
          </S.CampusCard>

          {/* Google Maps Component */}
          <S.MapWrapper>
            <S.GoogleMap 
              ref={mapRef}
              style={{ height: '250px', width: '100%' }}
            />
            {!isMapLoaded && (
              <S.MapLoading>
                <S.MapIcon style={{ fontSize: '2rem', color: '#3949ab' }}>📍</S.MapIcon>
                <S.MapText>Loading Map...</S.MapText>
              </S.MapLoading>
            )}
          </S.MapWrapper>

          <S.ContactEmail>
            <a href="mailto:info@uplusedu.com.au">
              info@uplusedu.com.au
            </a>
          </S.ContactEmail>
        </S.CampusInfoDesktop>
      </S.ContentWrapper>
      
      {/* QR Code 放大弹窗 */}
      {qrModalImage && (
        <S.QRModalOverlay>
          <S.QRModalContent>
            <S.QRModalCloseBtn onClick={() => setQrModalImage(null)}>&times;</S.QRModalCloseBtn>
            <S.QRModalImage src={qrModalImage} alt="QR Code" />
          </S.QRModalContent>
        </S.QRModalOverlay>
      )}

      {/* CSS Animation */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </S.Container>
  );
};

export default Contact;